<template>
  <div class="d-flex flex-nowrap overflow-auto crisisTableContainer">
    <div class="p-1 w-100 crisis-report-panel">
      <div class="d-flex">
        <h5>{{ $t('operational_security.crisis_form.employee_information_by_facility') }} -
          <span class="font-italic">{{ data.name }}-{{ data.reference_number }}</span>
        </h5>
      </div>

      <SkeletonLoading
        v-if="isMasterDataLoading"
        :count="1"
        :height="230"
        :fullWidth="true"
      />

      <div class="crisis-report-grid-shell">
        <crisis-report-grid
        v-if="!isMasterDataLoading && !dataNotExists"
        :source="gridRows"
        :columns="gridColumns"
        height="100%"
        />

        <div class="w-100 h-100 d-flex justify-content-center align-items-center" v-if="dataNotExists">
        <p class="text-center">No existe información para esta crisis</p>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import SkeletonLoading from "@/components/SkeletonLoading.vue";
import {computed, getCurrentInstance, onMounted, ref, watch} from '@vue/composition-api';
import axios from "@/libs/axios";
import {useCrisisFormUpdate} from "./composables/useCrisisFormUpdateSocket";
import CrisisReportGrid from './CrisisReportGrid.vue';
import {
  makeColumnGroup,
  makeGridColumn
} from './composables/useCrisisReportGrid';

export default {
  components: {
    CrisisReportGrid,
    SkeletonLoading
  },
  props: {
    data: {
      type: Object,
    },
    slotId: {
      type: [String, Number],
      default: null,
    },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy;
    const { lastUpdate } = useCrisisFormUpdate(props.data.id);
    const tableData = ref([]);
    const isMasterDataLoading = ref(false);
    const dataNotExists = ref(false);

    const tableColumns = [
      { key: 'soeid', label: 'alarm.timeline.soeid' },
      { key: 'nombre_empleado', label: 'operational_security.crisis_form.employee_name' },
      { key: 'geid', label: 'operational_security.crisis_form.geid' },
      { key: 'puesto', label: 'operational_security.crisis_form.employee_position' },
      { key: 'telefono', label: 'operational_security.crisis_form.contact_number' },
      { key: 'telefono_emergencia', label: 'operational_security.crisis_form.emergency_number' },
      { key: 'domicilio', label: 'operational_security.crisis_form.street_address_house_number' },
      { key: 'neighborhood', label: 'operational_security.crisis_form.neighborhood' },
      { key: 'municipality', label: 'operational_security.crisis_form.municipality' },
      { key: 'sirh', label: 'operational_security.crisis_form.assigned_sirh' },
      { key: 'sucursal', label: 'operational_security.crisis_form.facility_name' },
      {
        key: 'status_confirmation_call_tree',
        label: 'operational_security.crisis_form.status_confirmation_call_tree',
        children: [
          { key: 'me_encuentro_bien', label: 'operational_security.crisis_form.i_feel_fine' },
          { key: 'necesito_ayuda', label: 'operational_security.crisis_form.need_help' },
          { key: 'no_localizable', label: 'operational_security.crisis_form.unreachable' },
          { key: 'evacuado', label: 'operational_security.crisis_form.evacuated' },
          { key: 'por_error', label: 'operational_security.crisis_form.by_mistake' },
        ]
      },
      {
        key: 'contacted_by',
        label: 'operational_security.crisis_form.contacted_by',
        children: [
          { key: 'ccb', label: 'operational_security.crisis_form.ccb' },
          { key: 'business', label: 'operational_security.crisis_form.business' },
          { key: 'csis', label: 'operational_security.crisis_form.csis' },
        ]
      },
      {
        key: 'damages_and_needs',
        label: 'operational_security.crisis_form.damages_and_needs',
        children: [
          { key: 'lack_of_provisions', label: 'operational_security.crisis_form.shortage_of_food_supplies' },
          { key: 'emergency_plant', label: 'operational_security.crisis_form.emergency_plant' },
          { key: 'damaged_vehicle', label: 'operational_security.crisis_form.damaged_vehicle' },
          { key: 'lack_of_public_transportation', label: 'operational_security.crisis_form.lack_of_public_transportation' },
        ]
      },
    ];

    const gridColumns = computed(() => tableColumns.map(column => {
      if (!column.children) {
        return makeGridColumn({
          prop: column.key,
          name: vm.$t(column.label)
        });
      }

      return makeColumnGroup(
        vm.$t(column.label),
        column.children.map(child => makeGridColumn({
          prop: `${column.key}__${child.key}`,
          name: vm.$t(child.label)
        }))
      );
    }));

    const gridRows = computed(() => tableData.value.map(row => {
      const gridRow = {};

      tableColumns.forEach(column => {
        if (!column.children) {
          gridRow[column.key] = row[column.key];
          return;
        }

        column.children.forEach(child => {
          gridRow[`${column.key}__${child.key}`] = row[column.key]?.[child.key] ?? '';
        });
      });

      return gridRow;
    }));

    onMounted(() => {
      getEmployeeInformationByFacility();
    });

    watch(lastUpdate, () => getEmployeeInformationByFacility());

    async function getEmployeeInformationByFacility() {
      try {
        isMasterDataLoading.value = true;
        dataNotExists.value = false;
        const {data} = await axios.get(`/v1/operational-security-crisis/${props.data.id}/employee-report`);
        tableData.value = data.data || [];
      } catch (error) {
        dataNotExists.value = true;
      } finally {
        isMasterDataLoading.value = false;
      }
    }

    return {
      gridColumns,
      gridRows,
      isMasterDataLoading,
      dataNotExists
    };
  }
};
</script>

<style scoped lang="scss">
.crisisTableContainer {
  height: 100%;
  min-height: 0;
  background-color: #fff;
}

.crisis-report-panel {
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.crisis-report-grid-shell {
  flex: 1 1 auto;
  min-height: 420px;
  height: calc(100vh - 240px);
}

.crisis-report-filters {
  margin: 12px 0 16px;
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
}

.crisis-report-filter-group {
  margin: 0 18px 14px 0;
}

.crisis-report-city-list {
  margin: 8px 0 0 16px;
}

.crisis-report-filter-section {
  margin-top: 14px;
}
</style>
